/**
 * Search results display with grouping by entity type.
 * Shows modules, then pages, then tag matches, then entity groups — navigation
 * targets first (the destinations you know by name), data after.
 */
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { Loader2, Tag } from 'lucide-react';
import { SearchEntityResult, SearchTagResult } from '../../services/searchApi';
import { getTagFilteredListUrl } from '../../utils/entityUrls';
import { TAGGABLE_ENTITIES } from '../../../generated/taggable-entities';
import { getEntityByTable } from '../../config/modules.config';
import { NavSearchResult } from './searchConfig';
import { GlobalSearchResultItem } from './GlobalSearchResultItem';

const MAX_PER_GROUP = 3;

/** entity_type ({module}.{entity}) → its config table name. */
const TAGGABLE_TABLE_BY_TYPE = new Map(
  TAGGABLE_ENTITIES.map((info) => [info.entityType, info.table]),
);

/** A tag match resolved to a navigable list URL + display label. */
interface ResolvedTagResult {
  tag: SearchTagResult;
  url: string;
  pluralKey: string;
}

/**
 * Resolve a tag search result to a tag-filtered list URL plus the plural i18n
 * key of its entity type. Returns null when no list URL can be derived.
 */
function resolveTagResult(tag: SearchTagResult): ResolvedTagResult | null {
  const url = getTagFilteredListUrl(tag.entity_type, [tag.tag_id]);
  if (!url) return null;
  const table = TAGGABLE_TABLE_BY_TYPE.get(tag.entity_type);
  const pluralKey = table ? getEntityByTable(table)?.entity.pluralKey ?? '' : '';
  return { tag, url, pluralKey };
}

/**
 * Navigable URLs for tag matches, in render order — used by the search
 * containers to build their flat keyboard-navigation list.
 */
export function tagResultUrls(tags: SearchTagResult[]): string[] {
  return tags
    .map(resolveTagResult)
    .filter((r): r is ResolvedTagResult => r !== null)
    .map((r) => r.url);
}

interface GlobalSearchResultsProps {
  query: string;
  /** Matching modules — rendered above pages, mirroring the module picker. */
  modules?: NavSearchResult[];
  pages: NavSearchResult[];
  /** Pages offered before the user has typed anything. */
  suggestedPages?: NavSearchResult[];
  entities: SearchEntityResult[];
  tags: SearchTagResult[];
  /** Typo-tolerant "Did you mean…" matches (Tier 3); shown when results are sparse. */
  suggestions?: SearchEntityResult[];
  /** Matched query lexemes to highlight in result titles (Tier 3). */
  matchTerms?: string[];
  isLoading: boolean;
  selectedIndex: number;
  onSelect: (url: string) => void;
}

/**
 * Group entities by entity_type and return groups ordered by highest relevance.
 */
function groupEntities(entities: SearchEntityResult[]) {
  const groups: Record<string, SearchEntityResult[]> = {};
  for (const entity of entities) {
    const key = entity.entity_type;
    if (!groups[key]) groups[key] = [];
    groups[key].push(entity);
  }

  // Sort groups by the highest relevance score in each group
  return Object.entries(groups).sort((a, b) => {
    const aMax = Math.max(...a[1].map((e) => e.relevance_score));
    const bMax = Math.max(...b[1].map((e) => e.relevance_score));
    return bMax - aMax;
  });
}

/**
 * Fallback group label for an entity_type with no registry entry: turn the raw
 * table name into a readable string. e.g., "recruiting_candidates" -> "Candidates".
 * Only used when the entity type isn't found in the modules/entities config —
 * known types resolve to their translated plural label (see `groupLabelKey`).
 */
function formatGroupLabel(entityType: string): string {
  // Remove module prefix (everything before and including first underscore for known patterns)
  const parts = entityType.split('_');
  // Take the last meaningful part(s)
  const label = parts.length > 1 ? parts.slice(1).join(' ') : entityType;
  return label
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

/**
 * Translated, terminology-aware group header for an entity_type (table name).
 * Resolves the entity's `pluralKey` from the modules/entities registry and
 * translates it (honouring tenant terminology overrides like {{Organizations}}).
 * Falls back to a humanised table name when the type is not in the registry OR
 * its `pluralKey` translation is missing (`defaultValue`) — so a clean label is
 * always shown and a missing i18n key never leaks as a raw `entities.*` string.
 */
function groupLabel(entityType: string, t: TFunction): string {
  const fallback = formatGroupLabel(entityType);
  const pluralKey = getEntityByTable(entityType)?.entity.pluralKey;
  return pluralKey ? t(pluralKey, { defaultValue: fallback }) : fallback;
}

export function GlobalSearchResults({
  query,
  modules = [],
  pages,
  suggestedPages = [],
  entities,
  tags,
  suggestions = [],
  matchTerms = [],
  isLoading,
  selectedIndex,
  onSelect,
}: GlobalSearchResultsProps) {
  const { t } = useTranslation('common');
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());

  // Tag matches that resolve to a navigable list URL.
  const tagResults = useMemo(
    () =>
      tags
        .map(resolveTagResult)
        .filter((r): r is ResolvedTagResult => r !== null),
    [tags],
  );

  // Build flat list for keyboard navigation index tracking
  let currentIndex = 0;

  const toggleGroup = (groupKey: string) => {
    setExpandedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(groupKey)) {
        next.delete(groupKey);
      } else {
        next.add(groupKey);
      }
      return next;
    });
  };

  // Empty state — only when there are no suggestions to offer either.
  if (
    !isLoading &&
    query.length >= 2 &&
    modules.length === 0 &&
    pages.length === 0 &&
    entities.length === 0 &&
    tagResults.length === 0 &&
    suggestions.length === 0
  ) {
    return (
      <div className="py-6 text-center text-muted-foreground" data-testid="search-no-results">
        <p className="text-sm">{t('globalSearch.noResults')}</p>
        <p className="text-xs mt-1">{t('globalSearch.noResultsHint')}</p>
      </div>
    );
  }

  // No query yet — show suggested pages
  if (query.length < 2 && modules.length === 0 && pages.length === 0) {
    if (suggestedPages.length === 0) return null;
    return (
      <div className="max-h-[60vh] overflow-y-auto" data-testid="global-search-results">
        <div className="px-2 py-1" data-testid="search-result-group" data-group="suggested-pages">
          <div className="px-3 py-1.5 text-xs font-semibold text-muted-foreground uppercase tracking-wider">
            {t('globalSearch.suggestions')}
          </div>
          {suggestedPages.map((page, idx) => (
            <GlobalSearchResultItem
              key={`suggestion-${page.url}`}
              title={page.title}
              subtitle={null}
              iconNode={page.iconNode}
              moduleLabel={page.subtitle}
              resultKind="page"
              isSelected={selectedIndex === idx}
              onClick={() => onSelect(page.url)}
            />
          ))}
        </div>
      </div>
    );
  }

  const entityGroups = groupEntities(entities);

  return (
    <div className="max-h-[60vh] overflow-y-auto" data-testid="global-search-results">
      {/* Modules section — the module itself, opened at its default page. The
          subtitle is the module's description, so no module badge is repeated. */}
      {modules.length > 0 && (
        <div className="px-2 py-1" data-testid="search-result-group" data-group="modules">
          <div className="px-3 py-1.5 text-xs font-semibold text-muted-foreground uppercase tracking-wider">
            {t('modules.title')}
          </div>
          {modules.map((module) => {
            const idx = currentIndex++;
            return (
              <GlobalSearchResultItem
                key={`module-${module.moduleId}`}
                title={module.title}
                subtitle={module.subtitle}
                iconNode={module.iconNode}
                resultKind="module"
                isSelected={selectedIndex === idx}
                onClick={() => onSelect(module.url)}
              />
            );
          })}
        </div>
      )}

      {/* Pages section — the owning module is the badge, so it is not repeated
          as a subtitle line the way an entity's context is. */}
      {pages.length > 0 && (
        <div className="px-2 py-1" data-testid="search-result-group" data-group="pages">
          <div className="px-3 py-1.5 text-xs font-semibold text-muted-foreground uppercase tracking-wider">
            {t('globalSearch.pages')}
          </div>
          {pages.map((page) => {
            const idx = currentIndex++;
            return (
              <GlobalSearchResultItem
                key={`page-${page.url}`}
                title={page.title}
                subtitle={null}
                iconNode={page.iconNode}
                moduleLabel={page.subtitle}
                resultKind="page"
                isSelected={selectedIndex === idx}
                onClick={() => onSelect(page.url)}
              />
            );
          })}
        </div>
      )}

      {/* Tag matches — selecting one opens the tag-filtered list page */}
      {tagResults.length > 0 && (
        <div className="px-2 py-1" data-testid="search-result-group" data-group="tags">
          <div className="px-3 py-1.5 text-xs font-semibold text-muted-foreground uppercase tracking-wider">
            {t('tags.searchGroup')}
          </div>
          {tagResults.map(({ tag, url, pluralKey }) => {
            const idx = currentIndex++;
            const entityLabel = pluralKey ? t(pluralKey) : tag.entity_type;
            return (
              <GlobalSearchResultItem
                key={`tag-${tag.tag_id}-${tag.entity_type}`}
                title={tag.tag_name}
                highlightTerms={matchTerms}
                subtitle={t('tags.searchResultCount', {
                  count: tag.entity_count,
                  entity: entityLabel,
                })}
                iconNode={
                  <Tag
                    style={tag.tag_color ? { color: tag.tag_color } : undefined}
                  />
                }
                moduleLabel={t(tag.module_display_name, {
                  defaultValue: tag.module_id,
                })}
                resultKind="tag"
                dataEntityType={tag.entity_type}
                isSelected={selectedIndex === idx}
                onClick={() => onSelect(url)}
              />
            );
          })}
        </div>
      )}

      {/* Loading state */}
      {isLoading && entities.length === 0 && (
        <div className="flex items-center justify-center py-8">
          <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
        </div>
      )}

      {/* Entity groups */}
      {entityGroups.map(([groupKey, groupEntities]) => {
        const isExpanded = expandedGroups.has(groupKey);
        const displayCount = isExpanded ? groupEntities.length : MAX_PER_GROUP;
        const visibleEntities = groupEntities.slice(0, displayCount);
        const hasMore = groupEntities.length > MAX_PER_GROUP && !isExpanded;

        return (
          <div
            key={groupKey}
            className="px-2 py-1"
            data-testid="search-result-group"
            data-group="entities"
            data-entity-type={groupKey}
          >
            <div className="px-3 py-1.5 text-xs font-semibold text-muted-foreground uppercase tracking-wider">
              {groupLabel(groupKey, t)}
            </div>
            {visibleEntities.map((entity) => {
              const idx = currentIndex++;
              return (
                <GlobalSearchResultItem
                  key={`entity-${entity.entity_id}`}
                  title={entity.title}
                  subtitle={entity.subtitle}
                  icon={entity.icon}
                  highlightTerms={matchTerms}
                  moduleLabel={t(entity.module_display_name, { defaultValue: entity.module_id })}
                  resultKind="entity"
                  dataEntityType={entity.entity_type}
                  isSelected={selectedIndex === idx}
                  onClick={() => onSelect(entity.url)}
                />
              );
            })}
            {hasMore && (
              <button
                type="button"
                className="w-full px-3 py-1.5 text-xs text-primary hover:underline text-left"
                onClick={() => toggleGroup(groupKey)}
              >
                {t('globalSearch.showMore', { count: groupEntities.length - MAX_PER_GROUP })}
              </button>
            )}
          </div>
        );
      })}

      {/* "Did you mean…" — typo-tolerant fuzzy suggestions (Tier 3). */}
      {suggestions.length > 0 && (
        <div className="px-2 py-1" data-testid="search-did-you-mean">
          <div className="px-3 py-1.5 text-xs font-semibold text-muted-foreground uppercase tracking-wider">
            {t('globalSearch.didYouMean')}
          </div>
          {suggestions.map((entity) => {
            const idx = currentIndex++;
            return (
              <GlobalSearchResultItem
                key={`suggestion-${entity.entity_id}`}
                title={entity.title}
                subtitle={entity.subtitle}
                icon={entity.icon}
                highlightTerms={matchTerms}
                moduleLabel={t(entity.module_display_name, { defaultValue: entity.module_id })}
                resultKind="suggestion"
                dataEntityType={entity.entity_type}
                isSelected={selectedIndex === idx}
                onClick={() => onSelect(entity.url)}
              />
            );
          })}
        </div>
      )}
    </div>
  );
}
